import { Box, Container, Typography } from '@material-ui/core';
import React, { memo, useCallback, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import { useHistory } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import useNotifications from 'src/hooks/useNotifications';
import { OrgAppRolesProvider } from 'src/hooks/useOrgAppRoles';
import { addOrgUser } from 'src/services/apiService';
import { NewOrgUser } from 'src/types';
import AddOrgUserForm from '../AddOrgUserForm/AddOrgUserForm';
import GoBackButton from '../GoBackButton/GoBackButton';
import { useAddUserStyles } from './styles';

const AddUserPage: React.FC = () => {
  const history = useHistory();
  const classes = useAddUserStyles();

  const { user } = useAuth();

  const { addNotification } = useNotifications();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const navigateToUserList = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleSubmit = useCallback(
    async (data: NewOrgUser, callback: () => void, redirect = false) => {
      setIsLoading(true);
      try {
        await addOrgUser(user.organisationId, data);

        addNotification({
          title: `Invitation email sent to ${data.firstName} ${data.lastName}.`,
        });

        if (typeof callback === 'function') {
          callback();
        }

        setIsLoading(false);

        if (redirect) {
          navigateToUserList();
        }
      } catch (e) {
        setIsLoading(false);
        const userAlreadyExistsMessage =
          'The account is already associated with this organization.';
        const errorMessage = e.response?.data?.failed?.[0]?.error ?? '';

        const doesUserAlreadyExists = errorMessage.includes(
          userAlreadyExistsMessage
        );

        addNotification({
          title: `Failed to send invitation email to ${data.firstName} ${
            data.lastName
          }. ${doesUserAlreadyExists ? userAlreadyExistsMessage : ''}`.trim(),
          variant: 'error',
        });
      }
    },
    [addNotification, navigateToUserList, user.organisationId]
  );

  return (
    <Container disableGutters maxWidth="xl">
      <GoBackButton />

      <Box mt={3}>
        <Typography variant="h2" data-testid="page-title">
          Add new user
        </Typography>
      </Box>
      <Box mt={2}>
        <Typography
          variant="body1"
          className={classes.fix}
          data-testid="page-body-text"
        >
          Create one or more new users for your organisation{' '}
          <Box component="span" className={classes.emphasizedText}>
            {user.organisationName}
          </Box>
          .
          <br />
          <br />
          Fields marked with a{' '}
          <Box component="span" className={classes.dangerText}>
            *
          </Box>{' '}
          are required.
        </Typography>
      </Box>
      <OrgAppRolesProvider>
        <AddOrgUserForm
          onCancel={navigateToUserList}
          onSubmit={handleSubmit}
          isLoading={isLoading}
        />
      </OrgAppRolesProvider>
    </Container>
  );
};

export default hot(memo(AddUserPage));
